import { useCallback, useState } from 'react';
import type { SledTeam } from '@sdd-maturity/shared-types';

/**
 * Persists the user's most recent search-launcher selections so the empty-focus
 * dropdown can offer "pick up where you left off" entries. A lean snapshot of the
 * selected node is stored — enough to render (name + tier chip), resolve the parent
 * trail against the cached tree, and navigate via `buildScopePath` even before the
 * tree has loaded. Follows the `sdd-maturity-*` / try-catch localStorage pattern
 * used by `ThemeContext`.
 */

/** localStorage key holding the recent-selection list (matches `sdd-maturity-*` naming). */
export const STORAGE_KEY = 'sdd-maturity-recent-searches';

/** Keep only the 5 most recent selections. */
export const MAX_RECENTS = 5;

/** The stored slice of a selected node — sufficient to render, trail, and navigate. */
export type RecentSearch = Pick<SledTeam, 'number' | 'name' | 'tier' | 'tierLabel' | 'parentNumber'>;

/** Dedupe/identity key — mirrors the React key SearchLauncher uses for options. */
function keyOf(node: { tier: number; number: string }): string {
  return `${node.tier}-${node.number}`;
}

function isRecentSearch(value: unknown): value is RecentSearch {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.number === 'string' &&
    typeof v.name === 'string' &&
    typeof v.tier === 'number' &&
    typeof v.tierLabel === 'string' &&
    (v.parentNumber === undefined || typeof v.parentNumber === 'string')
  );
}

/** Read the persisted list, dropping malformed entries; `[]` when absent/invalid/unavailable. */
function readStored(): RecentSearch[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentSearch).slice(0, MAX_RECENTS);
  } catch {
    return [];
  }
}

function writeStored(recents: RecentSearch[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recents));
  } catch {
    // Persistence is best-effort; recents still work in-session.
  }
}

export interface UseRecentSearches {
  /** Most-recent-first, capped at {@link MAX_RECENTS}. */
  recents: RecentSearch[];
  /** Record a selection: move-to-front, dedupe by tier+number, cap, and persist. */
  addRecent: (node: RecentSearch) => void;
}

export function useRecentSearches(): UseRecentSearches {
  const [recents, setRecents] = useState<RecentSearch[]>(readStored);

  const addRecent = useCallback((node: RecentSearch): void => {
    const entry: RecentSearch = {
      number: node.number,
      name: node.name,
      tier: node.tier,
      tierLabel: node.tierLabel,
      parentNumber: node.parentNumber,
    };
    setRecents((prev) => {
      const next = [entry, ...prev.filter((r) => keyOf(r) !== keyOf(entry))].slice(0, MAX_RECENTS);
      writeStored(next);
      return next;
    });
  }, []);

  return { recents, addRecent };
}
